import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import ServiceChargeModal from './components/ServiceChargeModal';
import ProcessingModal from './components/ProcessingModal';
import EmailReceiptModal from './components/EmailReceiptModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lucide React Icons
import { ArrowLeft, Download, ChevronDown, ChevronUp } from 'lucide-react';

// UI Components
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow ${className}`}>{children}</div>
);

const CardHeader = ({ children, className = '' }) => (
  <div className={`p-6 ${className}`}>{children}</div>
);

const CardTitle = ({ children, className = '' }) => (
  <h2 className={`text-xl font-semibold ${className}`}>{children}</h2>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`p-6 pt-0 ${className}`}>{children}</div>
);

const Button = ({ children, onClick, disabled, className = '', type = 'button', variant = 'default' }) => {
  const baseClasses = 'px-6 py-3 rounded-lg font-semibold transition-all';
  const variantClasses = variant === 'outline' 
    ? 'border-2 border-slate-300 hover:bg-slate-50' 
    : 'bg-blue-600 hover:bg-blue-700 text-white';
  
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
    >
      {children}
    </button>
  );
};

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-sm font-semibold text-slate-700 mb-2">
    {children}
  </label>
);

const Input = ({ ...props }) => (
  <input
    {...props}
    className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
  />
);

const Textarea = ({ ...props }) => (
  <textarea
    {...props}
    className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
  />
);

const SMMKPage = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [progressSteps, setProgressSteps] = useState([
    { text: 'Analyzing book information', completed: false, current: false },
    { text: 'Generating social media content', completed: false, current: false },
    { text: 'Compiling marketing kit', completed: false, current: false }
  ]);
  const [pricingInfo, setPricingInfo] = useState(null);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [chargeInfo, setChargeInfo] = useState(null);
  const [showEmailReceiptModal, setShowEmailReceiptModal] = useState(false);
  const [lastTransactionId, setLastTransactionId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [generatingReport, setGeneratingReport] = useState(null);
  const [kitData, setKitData] = useState(null);
  const [viewingReport, setViewingReport] = useState(null);
  const [viewingReportContent, setViewingReportContent] = useState(null);
  const reportViewerRef = React.useRef(null);
  
  // Auto-scroll when report viewer appears
  React.useEffect(() => {
    if (viewingReport && viewingReportContent) {
      console.log('🎯 useEffect: Scrolling to report viewer');
      setTimeout(() => {
        // Scroll to report viewer card header (purple header)
        if (reportViewerRef.current) {
          console.log('📍 Scrolling report viewer to top');
          const rect = reportViewerRef.current.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const targetY = rect.top + scrollTop - 60; // 60px from top to show full header
          window.scrollTo({ top: targetY, behavior: 'smooth' });
        } else {
          // Fallback to anchor
          const anchor = document.getElementById('report-viewer-anchor');
          if (anchor) {
            console.log('📍 Using anchor fallback');
            const rect = anchor.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const targetY = rect.top + scrollTop - 100;
            window.scrollTo({ top: targetY, behavior: 'smooth' });
          }
        }
      }, 200);
    }
  }, [viewingReport, viewingReportContent]);
  
  const [formData, setFormData] = useState({
    book_title: '',
    subtitle: '',
    author_name: '',
    publisher_name: '',
    release_date: '',
    publisher_website: '',
    author_website: '',
    target_audience: '',
    publication_status: 'pre-launch',
    hardcover_url: '',
    hardcover_price: '',
    paperback_url: '',
    paperback_price: '',
    ebook_url: '',
    ebook_price: '',
    audiobook_url: '',
    audiobook_price: ''
  });
  
  const [manuscriptFile, setManuscriptFile] = useState(null);

  useEffect(() => {
    validateServiceAccess();
    
    // Load kit if viewing results
    if (location.state?.kitId) {
      loadKit(location.state.kitId);
    }
  }, [user, location.state]);
  
  const loadKit = async (kitId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/smmk/kits/${kitId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setKitData(response.data.kit);
      }
    } catch (error) {
      console.error('Error loading kit:', error);
      toast.error('Failed to load marketing kit');
    }
  };
  
  const validateServiceAccess = async () => {
    if (!user) return;
    
    try {
      const isExempt = user.role === 'admin' || user.role === 'beta_tester';
      const isSubscriber = user.subscription_status === 'monthly' || 
                          user.subscription_status === 'annual' ||
                          user.subscription_status === 'active';
      
      if (isExempt) {
        setPricingInfo({
          payment_required: false,
          price: 0,
          message: 'Free for admin and beta testers'
        });
      } else if (isSubscriber) {
        setPricingInfo({
          payment_required: true,
          price: 12.50,
          message: 'Subscriber rate: $12.50 (50% off regular price of $25.00)'
        });
      } else {
        setPricingInfo({
          payment_required: true,
          price: 25.00,
          message: 'À la carte rate: $25.00 per marketing kit'
        });
      }
    } catch (error) {
      console.error('Error validating service access:', error);
    }
  };
  
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 20 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 20MB.');
        e.target.value = '';
        return;
      }
      
      const allowedTypes = ['.txt', '.pdf', '.doc', '.docx'];
      const fileName = file.name.toLowerCase();
      const isAllowed = allowedTypes.some(type => fileName.endsWith(type));
      
      if (!isAllowed) {
        toast.error('Invalid file type. Please upload .txt, .pdf, .doc, or .docx files.');
        e.target.value = '';
        return;
      }
      
      setManuscriptFile(file);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.book_title || !formData.author_name || !formData.publisher_name || 
        !formData.target_audience || !manuscriptFile) {
      toast.error('Please fill in all required fields and upload a manuscript file');
      return;
    }
    
    const isExempt = user?.role === 'admin' || user?.role === 'beta_tester';
    
    if (isExempt) {
      await generateKit();
    } else {
      const isSubscriber = user?.subscription_status === 'monthly' ||
                          user?.subscription_status === 'annual' ||
                          user?.subscription_status === 'active';
      
      setChargeInfo({
        name: 'Social Media Marketing Kit',
        amount: isSubscriber ? 12.50 : 25.00,  // Premium service - $25 base, $12.50 subscriber
        description: `${formData.book_title} by ${formData.author_name}${isSubscriber ? ' (Subscriber - 50% off)' : ''}`,
        details: {
          book_title: formData.book_title,
          author: formData.author_name,
          publisher: formData.publisher_name
        }
      });
      setShowChargeModal(true);
    }
  };
  
  const handleChargeConfirm = async () => {
    setShowChargeModal(false);
    await generateKit();
  };
  
  const generateKit = async () => {
    setIsProcessing(true);
    
    // Step 1: Analyzing
    setCurrentStep('Analyzing book information and manuscript...');
    setProgressSteps([
      { text: 'Analyzing book information', completed: false, current: true },
      { text: 'Generating social media content', completed: false, current: false },
      { text: 'Compiling marketing kit', completed: false, current: false }
    ]);
    setProcessingProgress(10);
    
    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      formDataToSend.append('manuscript_file', manuscriptFile);
      
      setProcessingProgress(25);
      
      // Step 2: Generating
      setCurrentStep('Generating social media marketing content...');
      setProgressSteps([
        { text: 'Analyzing book information', completed: true, current: false },
        { text: 'Generating social media content', completed: false, current: true },
        { text: 'Compiling marketing kit', completed: false, current: false }
      ]);
      setProcessingProgress(40);
      
      const response = await axios.post(`${API}/smmk/submit`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Step 3: Compiling
      setCurrentStep('Compiling social media marketing kit...');
      setProgressSteps([
        { text: 'Analyzing book information', completed: true, current: false },
        { text: 'Generating social media content', completed: true, current: false },
        { text: 'Compiling marketing kit', completed: false, current: true }
      ]);
      setProcessingProgress(85);
      
      if (response.data.success) {
        if (response.data.transaction_id) {
          setLastTransactionId(response.data.transaction_id);
          setShowEmailReceiptModal(true);
        }
        
        // Complete
        setProgressSteps([
          { text: 'Analyzing book information', completed: true, current: false },
          { text: 'Generating social media content', completed: true, current: false },
          { text: 'Compiling marketing kit', completed: true, current: false }
        ]);
        setProcessingProgress(100);
        setCurrentStep('Complete!');
        
        toast.success('Social Media Marketing Kit generated successfully!');
        
        // Refresh dashboard stats after successful service completion
        window.dispatchEvent(new CustomEvent('refreshDashboardStats'));
        
        setTimeout(() => {
          if (response.data.kit_id) {
            setKitData({
              id: response.data.kit_id,
              ...formData,
              reports: {
                messaging_framework: {
                  generated: true,
                  content: response.data.primary_report
                }
              }
            });
          }
        }, 1000);
      } else {
        toast.error(response.data.error_message || 'Failed to generate kit');
      }
    } catch (error) {
      console.error('Generation error:', error);
      if (error.response?.status === 429) {
        toast.error('Rate limit exceeded. Please try again in an hour.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to generate marketing kit');
      }
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleGenerateReport = async (reportType, reportLabel) => {
    console.log('🔘 Button clicked:', reportType, reportLabel);
    console.log('📄 Report generated?', kitData.reports?.[reportType]?.generated);
    
    // Check if already generated
    if (kitData.reports?.[reportType]?.generated) {
      console.log('✅ Report already exists, displaying...');
      setViewingReport(reportType);
      setViewingReportContent(kitData.reports[reportType].content);
      // useEffect will handle scrolling
      return;
    }

    // Generate the report
    setGeneratingReport(reportType);
    
    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      formDataToSend.append('report_type', reportType);
      
      const response = await axios.post(
        `${API}/smmk/generate-report/${kitData.id}`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        const content = response.data.content;
        console.log('✅ Report generated successfully:', reportType);
        toast.success(`${reportLabel} generated successfully!`);
        
        // Update kit data
        setKitData(prev => ({
          ...prev,
          reports: {
            ...prev.reports,
            [reportType]: {
              generated: true,
              content: content
            }
          }
        }));
        
        // Set viewing state - useEffect will handle scrolling
        setViewingReport(reportType);
        setViewingReportContent(content);
      }
    } catch (error) {
      console.error('Report generation error:', error);
      toast.error('Failed to generate report');
    } finally {
      setGeneratingReport(null);
    }
  };
  
  const handleSendReceiptEmail = async (email) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/send-transaction-receipt`,
        {
          transaction_id: lastTransactionId,
          recipient_email: email
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Receipt sent successfully!');
    } catch (error) {
      toast.error('Failed to send receipt');
    }
  };

  const generatePrintHTML = () => {
    if (!viewingReport || !viewingReportContent || !kitData) return null;
    
    const reportTitles = {
      'messaging_framework': 'Messaging Framework',
      'platform_posts_1': 'Platform Posts - Facebook & Instagram',
      'platform_posts_2': 'Platform Posts - Twitter & TikTok',
      'creative_direction': 'Creative Direction & Visual Guide',
      'audience_insights': 'Audience Insights & Targeting',
      'action_plans': 'Action Plans',
      'launch_package': 'Launch Package & Review Templates',
      'influencer_outreach': 'Influencer & Partnership Outreach Kit',
      'email_campaign': 'Email Marketing Campaign',
      'hashtag_seo': 'Hashtag & SEO Strategy'
    };
    
    const reportTitle = reportTitles[viewingReport] || 'Report';
    
    const cleanText = (text) => {
      if (!text) return '';
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };
    
    let contentHTML = '';
    
    // Messaging Framework
    if (viewingReport === 'messaging_framework') {
      if (viewingReportContent.elevator_pitch) {
        contentHTML += `<div class="print-section highlight"><h3>🎯 Elevator Pitch</h3><p>${cleanText(viewingReportContent.elevator_pitch)}</p></div>`;
      }
      if (Array.isArray(viewingReportContent.taglines)) {
        contentHTML += `<div class="print-section"><h3>✨ Taglines</h3><ul>${viewingReportContent.taglines.map(t => `<li>${cleanText(t)}</li>`).join('')}</ul></div>`;
      }
      if (Array.isArray(viewingReportContent.key_messages)) {
        contentHTML += `<div class="print-section"><h3>💬 Key Messages</h3><ul>${viewingReportContent.key_messages.map(m => `<li>${cleanText(m)}</li>`).join('')}</ul></div>`;
      }
    }
    
    // Platform Posts
    else if (viewingReport === 'platform_posts_1' || viewingReport === 'platform_posts_2') {
      // Facebook
      if (viewingReportContent.facebook) {
        contentHTML += `<div class="print-section"><h3>👍 Facebook Posts</h3>`;
        if (Array.isArray(viewingReportContent.facebook)) {
          viewingReportContent.facebook.forEach((post, i) => {
            contentHTML += `<div class="post-box facebook"><strong>Post ${i+1}:</strong><br>${cleanText(post)}</div>`;
          });
        } else if (viewingReportContent.facebook.long_form_posts) {
          contentHTML += `<h4>Long-Form Posts:</h4>`;
          viewingReportContent.facebook.long_form_posts.forEach((post, i) => {
            contentHTML += `<div class="post-box facebook"><strong>${cleanText(post.type)}:</strong><br>${cleanText(post.content)}<br><em>CTA: ${cleanText(post.cta)}</em></div>`;
          });
        }
        contentHTML += `</div>`;
      }
      
      // Instagram
      if (viewingReportContent.instagram) {
        contentHTML += `<div class="print-section"><h3>📷 Instagram Posts</h3>`;
        if (Array.isArray(viewingReportContent.instagram)) {
          viewingReportContent.instagram.forEach((post, i) => {
            contentHTML += `<div class="post-box instagram"><strong>Post ${i+1}:</strong><br>${cleanText(post)}</div>`;
          });
        } else if (viewingReportContent.instagram.feed_captions) {
          contentHTML += `<h4>Feed Captions:</h4>`;
          viewingReportContent.instagram.feed_captions.forEach((post, i) => {
            contentHTML += `<div class="post-box instagram"><strong>${cleanText(post.post_type)}:</strong><br>${cleanText(post.caption)}</div>`;
          });
        }
        contentHTML += `</div>`;
      }
      
      // Twitter
      if (viewingReportContent.twitter) {
        contentHTML += `<div class="print-section"><h3>𝕏 Twitter/X Posts</h3>`;
        if (Array.isArray(viewingReportContent.twitter)) {
          viewingReportContent.twitter.forEach((post, i) => {
            contentHTML += `<div class="post-box twitter"><strong>Tweet ${i+1}:</strong><br>${cleanText(post)}</div>`;
          });
        } else if (viewingReportContent.twitter.tweet_threads) {
          viewingReportContent.twitter.tweet_threads.forEach((thread, i) => {
            contentHTML += `<div class="post-box twitter"><strong>${cleanText(thread.thread_topic)}:</strong><br>${thread.tweets.map(t => cleanText(t)).join('<br>')}</div>`;
          });
        }
        contentHTML += `</div>`;
      }
      
      // TikTok
      if (viewingReportContent.tiktok) {
        contentHTML += `<div class="print-section"><h3>🎵 TikTok Ideas</h3>`;
        if (Array.isArray(viewingReportContent.tiktok)) {
          viewingReportContent.tiktok.forEach((post, i) => {
            contentHTML += `<div class="post-box tiktok"><strong>Script ${i+1}:</strong><br>${cleanText(post)}</div>`;
          });
        } else if (viewingReportContent.tiktok.video_concepts) {
          viewingReportContent.tiktok.video_concepts.forEach((video, i) => {
            contentHTML += `<div class="post-box tiktok"><strong>${cleanText(video.concept)}:</strong><br>${cleanText(video.script)}</div>`;
          });
        }
        contentHTML += `</div>`;
      }
    }
    
    // Creative Direction
    else if (viewingReport === 'creative_direction') {
      if (viewingReportContent.design_palette) {
        contentHTML += `<div class="print-section"><h3>🎨 Design Palette</h3>`;
        if (viewingReportContent.design_palette.primary_colors && Array.isArray(viewingReportContent.design_palette.primary_colors)) {
          contentHTML += `<p><strong>Primary Colors:</strong> ${viewingReportContent.design_palette.primary_colors.join(', ')}</p>`;
        }
        if (viewingReportContent.design_palette.mood) {
          contentHTML += `<p><strong>Mood:</strong> ${cleanText(viewingReportContent.design_palette.mood)}</p>`;
        }
        if (viewingReportContent.design_palette.fonts && Array.isArray(viewingReportContent.design_palette.fonts)) {
          contentHTML += `<p><strong>Fonts:</strong> ${viewingReportContent.design_palette.fonts.join(', ')}</p>`;
        }
        contentHTML += `</div>`;
      }
      if (viewingReportContent.image_prompts) {
        contentHTML += `<div class="print-section"><h3>📸 Image Prompts</h3>`;
        if (viewingReportContent.image_prompts.instagram_posts && Array.isArray(viewingReportContent.image_prompts.instagram_posts)) {
          contentHTML += `<h4>Instagram Posts:</h4><ul>${viewingReportContent.image_prompts.instagram_posts.map(p => `<li>${cleanText(p)}</li>`).join('')}</ul>`;
        }
        if (viewingReportContent.image_prompts.facebook_banners && Array.isArray(viewingReportContent.image_prompts.facebook_banners)) {
          contentHTML += `<h4>Facebook Banners:</h4><ul>${viewingReportContent.image_prompts.facebook_banners.map(p => `<li>${cleanText(p)}</li>`).join('')}</ul>`;
        }
        contentHTML += `</div>`;
      }
      if (viewingReportContent.brand_cohesion) {
        contentHTML += `<div class="print-section"><h3>🎯 Brand Cohesion</h3>`;
        if (viewingReportContent.brand_cohesion.visual_themes && Array.isArray(viewingReportContent.brand_cohesion.visual_themes)) {
          contentHTML += `<h4>Visual Themes:</h4><ul>${viewingReportContent.brand_cohesion.visual_themes.map(t => `<li>${cleanText(t)}</li>`).join('')}</ul>`;
        }
        if (viewingReportContent.brand_cohesion.voice_guidelines) {
          contentHTML += `<h4>Voice Guidelines:</h4><p>${cleanText(viewingReportContent.brand_cohesion.voice_guidelines)}</p>`;
        }
        contentHTML += `</div>`;
      }
    }
    
    // Email Campaign
    else if (viewingReport === 'email_campaign') {
      if (viewingReportContent.pre_launch_sequence && Array.isArray(viewingReportContent.pre_launch_sequence)) {
        contentHTML += `<div class="print-section"><h3>📨 Pre-Launch Email Sequence</h3>`;
        viewingReportContent.pre_launch_sequence.forEach((email, i) => {
          contentHTML += `<div class="email-box"><strong>Email ${i+1} (${cleanText(email.timing || email.day || '')}):</strong><br><strong>Subject:</strong> ${cleanText(email.subject)}<br><br>${cleanText(email.content || email.body)}</div>`;
        });
        contentHTML += `</div>`;
      }
      if (viewingReportContent.launch_email) {
        contentHTML += `<div class="print-section"><h3>🚀 Launch Day Email</h3>`;
        contentHTML += `<div class="email-box highlight"><strong>Subject:</strong> ${cleanText(viewingReportContent.launch_email.subject)}<br><br>${cleanText(viewingReportContent.launch_email.content || viewingReportContent.launch_email.body)}</div>`;
        contentHTML += `</div>`;
      }
      if (viewingReportContent.post_launch_sequence && Array.isArray(viewingReportContent.post_launch_sequence)) {
        contentHTML += `<div class="print-section"><h3>📬 Post-Launch Follow-Up</h3>`;
        viewingReportContent.post_launch_sequence.forEach((email, i) => {
          contentHTML += `<div class="email-box"><strong>Email ${i+1} (${cleanText(email.timing || '')}):</strong><br><strong>Subject:</strong> ${cleanText(email.subject)}<br><br>${cleanText(email.content || email.body)}</div>`;
        });
        contentHTML += `</div>`;
      }
      if (viewingReportContent.newsletter_content_ideas && Array.isArray(viewingReportContent.newsletter_content_ideas)) {
        contentHTML += `<div class="print-section"><h3>📰 Newsletter Content Ideas</h3>`;
        contentHTML += `<ul>${viewingReportContent.newsletter_content_ideas.map(idea => `<li>${cleanText(idea)}</li>`).join('')}</ul>`;
        contentHTML += `</div>`;
      }
    }
    
    // Hashtag & SEO
    else if (viewingReport === 'hashtag_seo') {
      if (viewingReportContent.platform_hashtags) {
        contentHTML += `<div class="print-section"><h3>#️⃣ Platform Hashtags</h3>`;
        Object.entries(viewingReportContent.platform_hashtags).forEach(([platform, tags]) => {
          if (Array.isArray(tags)) {
            contentHTML += `<p><strong>${platform.charAt(0).toUpperCase() + platform.slice(1)}:</strong> ${tags.join(', ')}</p>`;
          }
        });
        contentHTML += `</div>`;
      }
      if (Array.isArray(viewingReportContent.seo_keywords)) {
        contentHTML += `<div class="print-section"><h3>🔍 SEO Keywords</h3><p>${viewingReportContent.seo_keywords.join(', ')}</p></div>`;
      }
      if (viewingReportContent.metadata_optimization) {
        contentHTML += `<div class="print-section"><h3>⚙️ Metadata Optimization</h3>`;
        const meta = viewingReportContent.metadata_optimization;
        if (meta.amazon_keywords && Array.isArray(meta.amazon_keywords)) {
          contentHTML += `<p><strong>Amazon Keywords:</strong> ${meta.amazon_keywords.join(', ')}</p>`;
        }
        if (meta.goodreads_shelves && Array.isArray(meta.goodreads_shelves)) {
          contentHTML += `<p><strong>Goodreads Shelves:</strong> ${meta.goodreads_shelves.join(', ')}</p>`;
        }
        if (meta.social_bio_keywords) {
          contentHTML += `<p><strong>Social Bio:</strong> ${cleanText(meta.social_bio_keywords)}</p>`;
        }
        contentHTML += `</div>`;
      }
    }
    
    // Audience Insights
    else if (viewingReport === 'audience_insights') {
      if (typeof viewingReportContent === 'object') {
        Object.entries(viewingReportContent).forEach(([key, value]) => {
          const title = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          contentHTML += `<div class="print-section"><h3>🎯 ${title}</h3>`;
          if (Array.isArray(value)) {
            contentHTML += `<ul>${value.map(item => `<li>${typeof item === 'object' ? cleanText(JSON.stringify(item)) : cleanText(item)}</li>`).join('')}</ul>`;
          } else if (typeof value === 'object') {
            contentHTML += `<pre style="font-size: 9pt; white-space: pre-wrap;">${cleanText(JSON.stringify(value, null, 2))}</pre>`;
          } else {
            contentHTML += `<p>${cleanText(value)}</p>`;
          }
          contentHTML += `</div>`;
        });
      }
    }
    
    // Action Plans
    else if (viewingReport === 'action_plans') {
      if (viewingReportContent.thirty_day_plan || viewingReportContent['30_day_prelaunch']) {
        const plan = viewingReportContent.thirty_day_plan || viewingReportContent['30_day_prelaunch'];
        contentHTML += `<div class="print-section"><h3>📅 30-Day Action Plan</h3>`;
        if (typeof plan === 'object') {
          Object.entries(plan).forEach(([key, actions]) => {
            contentHTML += `<div class="action-box"><strong>${key.replace(/_/g, ' ').toUpperCase()}:</strong>`;
            if (Array.isArray(actions)) {
              contentHTML += `<ul>${actions.map(a => `<li>${cleanText(a)}</li>`).join('')}</ul>`;
            } else {
              contentHTML += `<p>${cleanText(actions)}</p>`;
            }
            contentHTML += `</div>`;
          });
        }
        contentHTML += `</div>`;
      }
      if (viewingReportContent.ninety_day_plan || viewingReportContent['90_day_postlaunch']) {
        const plan = viewingReportContent.ninety_day_plan || viewingReportContent['90_day_postlaunch'];
        contentHTML += `<div class="print-section"><h3>📅 90-Day Action Plan</h3>`;
        if (typeof plan === 'object') {
          Object.entries(plan).forEach(([key, value]) => {
            contentHTML += `<div class="action-box"><strong>${key.replace(/_/g, ' ').toUpperCase()}:</strong>`;
            if (typeof value === 'object') {
              Object.entries(value).forEach(([k, v]) => {
                contentHTML += `<p><em>${k}:</em> `;
                if (Array.isArray(v)) {
                  contentHTML += v.map(i => cleanText(i)).join(', ');
                } else {
                  contentHTML += cleanText(v);
                }
                contentHTML += `</p>`;
              });
            } else {
              contentHTML += `<p>${cleanText(value)}</p>`;
            }
            contentHTML += `</div>`;
          });
        }
        contentHTML += `</div>`;
      }
    }
    
    // Launch Package
    else if (viewingReport === 'launch_package') {
      if (viewingReportContent.countdown_sequence && Array.isArray(viewingReportContent.countdown_sequence)) {
        contentHTML += `<div class="print-section"><h3>⏰ Countdown Sequence</h3>`;
        viewingReportContent.countdown_sequence.forEach((item) => {
          contentHTML += `<div class="post-box"><strong>${cleanText(item.days_out || item.timing)}:</strong><br>${cleanText(item.post || item.content)}</div>`;
        });
        contentHTML += `</div>`;
      }
      if (viewingReportContent.launch_day_schedule && Array.isArray(viewingReportContent.launch_day_schedule)) {
        contentHTML += `<div class="print-section"><h3>🚀 Launch Day Schedule</h3>`;
        viewingReportContent.launch_day_schedule.forEach((item) => {
          contentHTML += `<div class="post-box highlight"><strong>${cleanText(item.time)} - ${cleanText(item.platform)}:</strong><br>${cleanText(item.post)}</div>`;
        });
        contentHTML += `</div>`;
      }
      if (viewingReportContent.review_templates || viewingReportContent.review_request_templates) {
        const templates = viewingReportContent.review_templates || viewingReportContent.review_request_templates;
        contentHTML += `<div class="print-section"><h3>⭐ Review Request Templates</h3>`;
        if (Array.isArray(templates)) {
          templates.forEach((template, i) => {
            contentHTML += `<div class="email-box"><strong>Template ${i+1}:</strong><br>${cleanText(template)}</div>`;
          });
        } else if (typeof templates === 'object') {
          Object.entries(templates).forEach(([key, value]) => {
            contentHTML += `<div class="email-box"><strong>${key.replace(/_/g, ' ')}:</strong><br>${Array.isArray(value) ? value.map(v => cleanText(v)).join('<br><br>') : cleanText(value)}</div>`;
          });
        }
        contentHTML += `</div>`;
      }
    }
    
    // Influencer Outreach
    else if (viewingReport === 'influencer_outreach') {
      if (viewingReportContent.pitch_templates && Array.isArray(viewingReportContent.pitch_templates)) {
        contentHTML += `<div class="print-section"><h3>📝 Pitch Templates</h3>`;
        viewingReportContent.pitch_templates.forEach((template) => {
          contentHTML += `<div class="email-box"><strong>${cleanText(template.title || 'Pitch Template')}:</strong><br>${cleanText(template.content || template)}</div>`;
        });
        contentHTML += `</div>`;
      }
      if (viewingReportContent.partnership_proposals) {
        contentHTML += `<div class="print-section"><h3>🤝 Partnership Proposals</h3>`;
        if (typeof viewingReportContent.partnership_proposals === 'object') {
          Object.entries(viewingReportContent.partnership_proposals).forEach(([key, value]) => {
            contentHTML += `<div class="post-box"><strong>${key.replace(/_/g, ' ').toUpperCase()}:</strong><br>${cleanText(value)}</div>`;
          });
        }
        contentHTML += `</div>`;
      }
      if (viewingReportContent.collaboration_ideas && Array.isArray(viewingReportContent.collaboration_ideas)) {
        contentHTML += `<div class="print-section"><h3>💡 Collaboration Ideas</h3>`;
        contentHTML += `<ul>${viewingReportContent.collaboration_ideas.map(idea => `<li>${cleanText(idea)}</li>`).join('')}</ul>`;
        contentHTML += `</div>`;
      }
    }
    
    // Fallback for any undefined report types
    else {
      contentHTML += `<div class="print-section"><p><em>Full report content available on-screen. This is a summary view for printing.</em></p></div>`;
    }
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${reportTitle}</title>
        <meta charset="UTF-8">
        <style>
          body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
          @page { margin: 0.4in; size: letter; }
          .header { border: 2px solid #000; padding: 8px; margin-bottom: 8px; }
          .logo-img { text-align: center; margin-bottom: 4px; }
          .logo-img img { height: 40px; max-width: 100%; display: inline-block; }
          .report-title { font-size: 16pt; font-weight: bold; text-align: center; border-bottom: 1px solid #000; padding-bottom: 6px; margin: 8px 0; }
          .metadata { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; font-size: 9pt; line-height: 1.3; }
          h2 { font-size: 14pt; font-weight: bold; margin: 8pt 0 6pt 0; }
          h3 { font-size: 12pt; font-weight: bold; margin: 6pt 0 4pt 0; }
          h4 { font-size: 10pt; font-weight: bold; margin: 4pt 0 3pt 0; }
          p { font-size: 10pt; line-height: 1.4; margin: 0 0 6pt 0; white-space: pre-wrap; }
          ul, ol { font-size: 10pt; line-height: 1.4; margin: 4pt 0; padding-left: 20pt; }
          li { margin-bottom: 3pt; }
          .print-section { border-bottom: 1px solid #ddd; padding-bottom: 6pt; margin-bottom: 8pt; page-break-inside: avoid; }
          .print-section:last-child { border-bottom: none; }
          .post-box { background: #f8f9fa; padding: 8pt; margin: 6pt 0; border-left: 3pt solid #6c757d; }
          .post-box.facebook { background: #f0f4ff; border-left-color: #1877f2; }
          .post-box.instagram { background: #fff5f8; border-left-color: #e1306c; }
          .post-box.twitter { background: #f0f8ff; border-left-color: #1da1f2; }
          .post-box.tiktok { background: #fff0f5; border-left-color: #000000; }
          .email-box { background: #f0f7ff; padding: 8pt; margin: 6pt 0; border: 1px solid #007bff; }
          .action-box { background: #f0fff4; padding: 8pt; margin: 6pt 0; border-left: 3pt solid #28a745; }
          .highlight { background: #fffbf0; border-left: 3pt solid #ffc107; }
          .footer { margin-top: 12pt; padding-top: 8pt; text-align: center; font-size: 8pt; color: #666; border-top: 1px solid #ddd; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo-img">
            <img src="https://customer-assets.emergentagent.com/job_authoring-hub/artifacts/5v7reazk_Digital_Ink_AI_Logo.jpg" alt="Digital Ink AI" />
          </div>
          <h1 class="report-title">Social Media Marketing Kit<br>${reportTitle}</h1>
          <div class="metadata">
            <div><strong>Book Title:</strong> ${cleanText(kitData.book_title || 'N/A')}</div>
            <div><strong>Author:</strong> ${cleanText(kitData.author_name || 'N/A')}</div>
            <div><strong>Publisher:</strong> ${cleanText(kitData.publisher_name || 'N/A')}</div>
            <div><strong>Release Date:</strong> ${cleanText(kitData.release_date || 'Not specified')}</div>
            <div style="grid-column: span 2;"><strong>Target Audience:</strong> ${cleanText(kitData.target_audience || 'Not specified')}</div>
          </div>
        </div>
        ${contentHTML}
        <div class="footer">Generated by Digital Ink AI - www.DigitalInkAI.com</div>
      </body>
      </html>
    `;
  };

  const handlePrintReport = () => {
    try {
      if (!viewingReport || !viewingReportContent) {
        toast.error('No report selected for printing');
        return;
      }
      
      const printHTML = generatePrintHTML();
      if (!printHTML) {
        toast.error('Unable to generate print preview');
        return;
      }
      
      // Open in new window (allows user to see full preview)
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(printHTML);
        printWindow.document.close();
        
        // Wait for images and content to load, then print
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      } else {
        toast.error('Unable to open print window. Please check popup blocker.');
      }
      
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to print report');
    }
  };

  const handleDownloadReport = () => {
    try {
      if (!viewingReport || !viewingReportContent) {
        toast.error('No report selected for download');
        return;
      }
      
      const reportTitles = {
        'messaging_framework': 'Messaging Framework',
        'platform_posts_1': 'Platform Posts - Facebook & Instagram',
        'platform_posts_2': 'Platform Posts - Twitter & TikTok',
        'creative_direction': 'Creative Direction & Visual Guide',
        'audience_insights': 'Audience Insights & Targeting',
        'action_plans': 'Action Plans',
        'launch_package': 'Launch Package & Review Templates',
        'influencer_outreach': 'Influencer & Partnership Outreach Kit',
        'email_campaign': 'Email Marketing Campaign',
        'hashtag_seo': 'Hashtag & SEO Strategy'
      };
      
      const reportTitle = reportTitles[viewingReport] || 'Report';
      const fileName = `SMMK-${kitData.book_title.replace(/[^a-z0-9]/gi, '_')}-${viewingReport}-${Date.now()}.html`;
      
      // Generate HTML content
      const htmlContent = generatePrintHTML();
      if (!htmlContent) {
        toast.error('Unable to generate download file');
        return;
      }
      
      // Create blob and download
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully!');
      
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download report');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // If viewing results
  if (kitData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100">
        {/* Header with Logo and Navigation */}
        <header className="bg-white shadow-sm sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img 
                  src="/Digital_Ink_AI_Logo.png" 
                  alt="Digital Ink AI Logo" 
                  className="h-12 w-auto"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                  }}
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => navigate('/homepage')}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  🏠 Home
                </button>
                <button
                  onClick={() => navigate('/user_dashboard')}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  📊 Dashboard
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header - Matching MMKG Style */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="border-b pb-4 mb-4">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Social Media Marketing Kit</h2>
                  <p className="text-slate-600 mt-1">Complete Social Media Campaign for {kitData.book_title}</p>
                  <p className="text-slate-600 text-sm">by {kitData.author_name}</p>
                </div>
              </div>
            </div>
            
            {/* Metadata Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Book Title:</span>
                <p className="font-semibold text-slate-900">{kitData.book_title || 'N/A'}</p>
              </div>
              <div>
                <span className="text-slate-500">Author Name:</span>
                <p className="font-semibold text-slate-900">{kitData.author_name || 'N/A'}</p>
              </div>
              <div>
                <span className="text-slate-500">Publisher:</span>
                <p className="font-semibold text-slate-900">{kitData.publisher_name || 'N/A'}</p>
              </div>
              <div>
                <span className="text-slate-500">Release Date:</span>
                <p className="font-semibold text-slate-900">{kitData.release_date || 'N/A'}</p>
              </div>
              <div>
                <span className="text-slate-500">Target Audience:</span>
                <p className="font-semibold text-slate-900">{kitData.target_audience || 'N/A'}</p>
              </div>
              <div>
                <span className="text-slate-500">Book Genre:</span>
                <p className="font-semibold text-slate-900">{kitData.book_genre || 'N/A'}</p>
              </div>
              <div>
                <span className="text-slate-500">Created:</span>
                <p className="font-semibold text-slate-900">{new Date(kitData.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <span className="text-slate-500">Kit ID:</span>
                <p className="font-semibold text-slate-900 text-xs">{kitData.id.substring(0, 8)}...</p>
              </div>
            </div>
          </div>

          {/* Reports Grid */}
          <Card className="border-l-4 border-purple-500">
            <CardHeader>
              <CardTitle className="text-purple-900">📱 Social Media Marketing Materials</CardTitle>
              <p className="text-sm text-slate-600 mt-2">Click any report name to generate it on-demand. Once generated, you can view the full content below.</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {/* Report 1: Messaging Framework (already generated) */}
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center space-x-3 flex-1">
                    <span className="text-2xl">📝</span>
                    <div className="flex-1">
                      <div className="font-bold text-slate-900 flex items-center gap-2">
                        <span className="text-green-600">✓</span>
                        Messaging Framework
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Core messaging (already generated)</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      console.log('🔘 Messaging Framework View Report clicked');
                      setViewingReport('messaging_framework');
                      setViewingReportContent(kitData.reports?.messaging_framework?.content);
                      // useEffect will handle scrolling
                    }}
                    className="px-4 py-2 text-sm rounded bg-purple-600 text-white hover:bg-purple-700"
                  >
                    📖 View Report
                  </button>
                </div>

                {/* Report 2: Platform Posts 1 (Facebook/Instagram) */}
                <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  kitData.reports?.platform_posts_1?.generated ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                }`}>
                  <div className="flex items-center space-x-3 flex-1">
                    <span className="text-2xl">📱</span>
                    <div className="flex-1">
                      <div className={`flex items-center gap-2 ${kitData.reports?.platform_posts_1?.generated ? 'font-bold' : ''}`}>
                        {kitData.reports?.platform_posts_1?.generated && <span className="text-green-600">✓</span>}
                        <span>Platform Posts - Facebook & Instagram</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {kitData.reports?.platform_posts_1?.generated ? 'Report ready' : 'Click View Report to generate'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleGenerateReport('platform_posts_1', 'Platform Posts - Facebook & Instagram')}
                    disabled={generatingReport !== null}
                    className={`px-4 py-2 text-sm rounded ${
                      generatingReport !== null
                        ? 'bg-slate-400 text-white cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {generatingReport === 'platform_posts_1' ? '⚙️ Generating...' : kitData.reports?.platform_posts_1?.generated ? '📖 View Report' : '📥 Get Report'}
                  </button>
                </div>

                {/* Report 3: Platform Posts 2 (Twitter/TikTok) */}
                <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  kitData.reports?.platform_posts_2?.generated ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                }`}>
                  <div className="flex items-center space-x-3 flex-1">
                    <span className="text-2xl">𝕏</span>
                    <div className="flex-1">
                      <div className={`flex items-center gap-2 ${kitData.reports?.platform_posts_2?.generated ? 'font-bold' : ''}`}>
                        {kitData.reports?.platform_posts_2?.generated && <span className="text-green-600">✓</span>}
                        <span>Platform Posts - Twitter & TikTok</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {kitData.reports?.platform_posts_2?.generated ? 'Report ready' : 'Click View Report to generate'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleGenerateReport('platform_posts_2', 'Platform Posts - Twitter & TikTok')}
                    disabled={generatingReport !== null}
                    className={`px-4 py-2 text-sm rounded ${
                      generatingReport !== null
                        ? 'bg-slate-400 text-white cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {generatingReport === 'platform_posts_2' ? '⚙️ Generating...' : kitData.reports?.platform_posts_2?.generated ? '📖 View Report' : '📥 Get Report'}
                  </button>
                </div>

                {/* Report 4: Creative Direction */}
                <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  kitData.reports?.creative_direction?.generated ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                }`}>
                  <div className="flex items-center space-x-3 flex-1">
                    <span className="text-2xl">🎨</span>
                    <div className="flex-1">
                      <div className={`flex items-center gap-2 ${kitData.reports?.creative_direction?.generated ? 'font-bold' : ''}`}>
                        {kitData.reports?.creative_direction?.generated && <span className="text-green-600">✓</span>}
                        <span>Creative Direction & Visual Guide</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {kitData.reports?.creative_direction?.generated ? 'Report ready' : 'Click View Report to generate'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleGenerateReport('creative_direction', 'Creative Direction & Visual Guide')}
                    disabled={generatingReport !== null}
                    className={`px-4 py-2 text-sm rounded ${
                      generatingReport !== null
                        ? 'bg-slate-400 text-white cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {generatingReport === 'creative_direction' ? '⚙️ Generating...' : kitData.reports?.creative_direction?.generated ? '📖 View Report' : '📥 Get Report'}
                  </button>
                </div>

                {/* Report 5: Audience Insights */}
                <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  kitData.reports?.audience_insights?.generated ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                }`}>
                  <div className="flex items-center space-x-3 flex-1">
                    <span className="text-2xl">🎯</span>
                    <div className="flex-1">
                      <div className={`flex items-center gap-2 ${kitData.reports?.audience_insights?.generated ? 'font-bold' : ''}`}>
                        {kitData.reports?.audience_insights?.generated && <span className="text-green-600">✓</span>}
                        <span>Audience Insights & Targeting</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {kitData.reports?.audience_insights?.generated ? 'Report ready' : 'Click View Report to generate'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleGenerateReport('audience_insights', 'Audience Insights & Targeting')}
                    disabled={generatingReport !== null}
                    className={`px-4 py-2 text-sm rounded ${
                      generatingReport !== null
                        ? 'bg-slate-400 text-white cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {generatingReport === 'audience_insights' ? '⚙️ Generating...' : kitData.reports?.audience_insights?.generated ? '📖 View Report' : '📥 Get Report'}
                  </button>
                </div>

                {/* Report 6: Action Plans */}
                <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  kitData.reports?.action_plans?.generated ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                }`}>
                  <div className="flex items-center space-x-3 flex-1">
                    <span className="text-2xl">📅</span>
                    <div className="flex-1">
                      <div className={`flex items-center gap-2 ${kitData.reports?.action_plans?.generated ? 'font-bold' : ''}`}>
                        {kitData.reports?.action_plans?.generated && <span className="text-green-600">✓</span>}
                        <span>Action Plans (30-day & 90-day)</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {kitData.reports?.action_plans?.generated ? 'Report ready' : 'Click View Report to generate'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleGenerateReport('action_plans', 'Action Plans')}
                    disabled={generatingReport !== null}
                    className={`px-4 py-2 text-sm rounded ${
                      generatingReport !== null
                        ? 'bg-slate-400 text-white cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {generatingReport === 'action_plans' ? '⚙️ Generating...' : kitData.reports?.action_plans?.generated ? '📖 View Report' : '📥 Get Report'}
                  </button>
                </div>

                {/* Report 7: Launch Package */}
                <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  kitData.reports?.launch_package?.generated ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                }`}>
                  <div className="flex items-center space-x-3 flex-1">
                    <span className="text-2xl">🚀</span>
                    <div className="flex-1">
                      <div className={`flex items-center gap-2 ${kitData.reports?.launch_package?.generated ? 'font-bold' : ''}`}>
                        {kitData.reports?.launch_package?.generated && <span className="text-green-600">✓</span>}
                        <span>Launch Package & Review Templates</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {kitData.reports?.launch_package?.generated ? 'Report ready' : 'Click View Report to generate'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleGenerateReport('launch_package', 'Launch Package & Review Templates')}
                    disabled={generatingReport !== null}
                    className={`px-4 py-2 text-sm rounded ${
                      generatingReport !== null
                        ? 'bg-slate-400 text-white cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {generatingReport === 'launch_package' ? '⚙️ Generating...' : kitData.reports?.launch_package?.generated ? '📖 View Report' : '📥 Get Report'}
                  </button>
                </div>

                {/* Report 8: Influencer Outreach */}
                <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  kitData.reports?.influencer_outreach?.generated ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                }`}>
                  <div className="flex items-center space-x-3 flex-1">
                    <span className="text-2xl">🤝</span>
                    <div className="flex-1">
                      <div className={`flex items-center gap-2 ${kitData.reports?.influencer_outreach?.generated ? 'font-bold' : ''}`}>
                        {kitData.reports?.influencer_outreach?.generated && <span className="text-green-600">✓</span>}
                        <span>Influencer & Partnership Outreach Kit</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {kitData.reports?.influencer_outreach?.generated ? 'Report ready' : 'Click View Report to generate'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleGenerateReport('influencer_outreach', 'Influencer & Partnership Outreach Kit')}
                    disabled={generatingReport !== null}
                    className={`px-4 py-2 text-sm rounded ${
                      generatingReport !== null
                        ? 'bg-slate-400 text-white cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {generatingReport === 'influencer_outreach' ? '⚙️ Generating...' : kitData.reports?.influencer_outreach?.generated ? '📖 View Report' : '📥 Get Report'}
                  </button>
                </div>

                {/* Report 9: Email Marketing Campaign */}
                <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  kitData.reports?.email_campaign?.generated ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                }`}>
                  <div className="flex items-center space-x-3 flex-1">
                    <span className="text-2xl">📧</span>
                    <div className="flex-1">
                      <div className={`flex items-center gap-2 ${kitData.reports?.email_campaign?.generated ? 'font-bold' : ''}`}>
                        {kitData.reports?.email_campaign?.generated && <span className="text-green-600">✓</span>}
                        <span>Email Marketing Campaign</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {kitData.reports?.email_campaign?.generated ? 'Report ready' : 'Click View Report to generate'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleGenerateReport('email_campaign', 'Email Marketing Campaign')}
                    disabled={generatingReport !== null}
                    className={`px-4 py-2 text-sm rounded ${
                      generatingReport !== null
                        ? 'bg-slate-400 text-white cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {generatingReport === 'email_campaign' ? '⚙️ Generating...' : kitData.reports?.email_campaign?.generated ? '📖 View Report' : '📥 Get Report'}
                  </button>
                </div>

                {/* Report 10: Hashtag & SEO Strategy */}
                <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  kitData.reports?.hashtag_seo?.generated ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                }`}>
                  <div className="flex items-center space-x-3 flex-1">
                    <span className="text-2xl">#️⃣</span>
                    <div className="flex-1">
                      <div className={`flex items-center gap-2 ${kitData.reports?.hashtag_seo?.generated ? 'font-bold' : ''}`}>
                        {kitData.reports?.hashtag_seo?.generated && <span className="text-green-600">✓</span>}
                        <span>Hashtag & SEO Strategy</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {kitData.reports?.hashtag_seo?.generated ? 'Report ready' : 'Click View Report to generate'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleGenerateReport('hashtag_seo', 'Hashtag & SEO Strategy')}
                    disabled={generatingReport !== null}
                    className={`px-4 py-2 text-sm rounded ${
                      generatingReport !== null
                        ? 'bg-slate-400 text-white cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {generatingReport === 'hashtag_seo' ? '⚙️ Generating...' : kitData.reports?.hashtag_seo?.generated ? '📖 View Report' : '📥 Get Report'}
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* PRIMARY anchor for autoscrolling - positioned right after reports list */}
          <div id="report-viewer-anchor" className="h-4"></div>

          {/* Horizontal divider */}
          <div className="border-t-2 border-slate-300 my-6"></div>

          {/* Report Viewer Section - Shows selected report content */}
          {console.log('🎨 Rendering check - viewingReport:', viewingReport, 'viewingReportContent exists:', !!viewingReportContent)}
          {viewingReport && viewingReportContent && (
            <Card ref={reportViewerRef} className="report-viewer-card border-l-4 border-purple-500 bg-gradient-to-br from-purple-50 to-white">
              <CardHeader className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">📄</span>
                    <CardTitle className="text-white">
                      {viewingReport === 'messaging_framework' && 'Messaging Framework'}
                      {viewingReport === 'platform_posts_1' && 'Platform Posts - Facebook & Instagram'}
                      {viewingReport === 'platform_posts_2' && 'Platform Posts - Twitter & TikTok'}
                      {viewingReport === 'creative_direction' && 'Creative Direction & Visual Guide'}
                      {viewingReport === 'audience_insights' && 'Audience Insights & Targeting'}
                      {viewingReport === 'action_plans' && 'Action Plans'}
                      {viewingReport === 'launch_package' && 'Launch Package & Review Templates'}
                      {viewingReport === 'influencer_outreach' && 'Influencer & Partnership Outreach Kit'}
                      {viewingReport === 'email_campaign' && 'Email Marketing Campaign'}
                      {viewingReport === 'hashtag_seo' && 'Hashtag & SEO Strategy'}
                    </CardTitle>
                  </div>
                  <button
                    onClick={() => {
                      setViewingReport(null);
                      setViewingReportContent(null);
                    }}
                    className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-sm"
                  >
                    ✕ Close
                  </button>
                </div>
              </CardHeader>
              <CardContent className="prose max-w-none p-6">
                {/* Messaging Framework */}
                {viewingReport === 'messaging_framework' && (
                  <div className="space-y-4">
                    {viewingReportContent.elevator_pitch && (
                      <div className="bg-blue-50 p-4 rounded border-l-4 border-blue-500">
                        <h3 className="text-lg font-bold text-blue-900">Elevator Pitch</h3>
                        <p className="text-slate-700">{viewingReportContent.elevator_pitch}</p>
                      </div>
                    )}
                    {viewingReportContent.taglines && Array.isArray(viewingReportContent.taglines) && (
                      <div>
                        <h3 className="text-lg font-bold text-purple-900">Taglines</h3>
                        <ul className="list-disc list-inside space-y-1 mt-2">
                          {viewingReportContent.taglines.map((tagline, i) => (
                            <li key={i} className="text-slate-700">{tagline}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {viewingReportContent.key_messages && Array.isArray(viewingReportContent.key_messages) && (
                      <div>
                        <h3 className="text-lg font-bold text-purple-900">Key Messages</h3>
                        <ul className="list-disc list-inside space-y-1 mt-2">
                          {viewingReportContent.key_messages.map((msg, i) => (
                            <li key={i} className="text-slate-700">{msg}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Platform Posts - Handle both old complex and new simple structures */}
                {(viewingReport === 'platform_posts_1' || viewingReport === 'platform_posts_2') && (
                  <div className="space-y-6">
                    {/* Facebook Posts */}
                    {viewingReportContent.facebook && (
                      <div>
                        <h3 className="text-lg font-bold text-purple-900 flex items-center gap-2">
                          <span className="text-blue-700">👍</span> Facebook Posts
                        </h3>
                        <div className="space-y-3 mt-2">
                          {/* New format: simple array */}
                          {Array.isArray(viewingReportContent.facebook) && viewingReportContent.facebook.map((post, i) => (
                            <div key={i} className="bg-blue-50 p-3 rounded border-l-4 border-blue-700">
                              <p className="text-slate-700 text-sm whitespace-pre-wrap">{post}</p>
                            </div>
                          ))}
                          {/* Old format: complex object */}
                          {!Array.isArray(viewingReportContent.facebook) && viewingReportContent.facebook.long_form_posts && (
                            <>
                              <div className="font-semibold text-blue-900 mt-2">Long-Form Posts:</div>
                              {viewingReportContent.facebook.long_form_posts.map((post, i) => (
                                <div key={`lf-${i}`} className="bg-blue-50 p-3 rounded border-l-4 border-blue-700">
                                  <div className="font-medium text-blue-800 mb-1">{post.type}</div>
                                  <p className="text-slate-700 text-sm whitespace-pre-wrap">{post.content}</p>
                                  {post.cta && <div className="text-blue-600 font-medium mt-2">CTA: {post.cta}</div>}
                                </div>
                              ))}
                            </>
                          )}
                          {!Array.isArray(viewingReportContent.facebook) && viewingReportContent.facebook.short_posts && (
                            <>
                              <div className="font-semibold text-blue-900 mt-3">Short Posts:</div>
                              {viewingReportContent.facebook.short_posts.map((post, i) => (
                                <div key={`sf-${i}`} className="bg-blue-100 p-2 rounded">
                                  <p className="text-slate-700 text-sm">{post}</p>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Instagram Posts */}
                    {viewingReportContent.instagram && (
                      <div>
                        <h3 className="text-lg font-bold text-purple-900 flex items-center gap-2">
                          <span className="text-pink-500">📷</span> Instagram Posts
                        </h3>
                        <div className="space-y-3 mt-2">
                          {/* New format: simple array */}
                          {Array.isArray(viewingReportContent.instagram) && viewingReportContent.instagram.map((post, i) => (
                            <div key={i} className="bg-pink-50 p-3 rounded border-l-4 border-pink-500">
                              <p className="text-slate-700 text-sm whitespace-pre-wrap">{post}</p>
                            </div>
                          ))}
                          {/* Old format: complex object */}
                          {!Array.isArray(viewingReportContent.instagram) && viewingReportContent.instagram.feed_captions && (
                            <>
                              <div className="font-semibold text-pink-900 mt-2">Feed Captions:</div>
                              {viewingReportContent.instagram.feed_captions.map((post, i) => (
                                <div key={`fc-${i}`} className="bg-pink-50 p-3 rounded border-l-4 border-pink-500">
                                  <div className="font-medium text-pink-800 mb-1">{post.post_type}</div>
                                  {post.hook && <div className="text-pink-600 italic text-sm mb-1">Hook: {post.hook}</div>}
                                  <p className="text-slate-700 text-sm whitespace-pre-wrap">{post.caption}</p>
                                </div>
                              ))}
                            </>
                          )}
                          {!Array.isArray(viewingReportContent.instagram) && viewingReportContent.instagram.reels_scripts && (
                            <>
                              <div className="font-semibold text-pink-900 mt-3">Reels Scripts:</div>
                              {viewingReportContent.instagram.reels_scripts.map((reel, i) => (
                                <div key={`rs-${i}`} className="bg-pink-100 p-3 rounded">
                                  <div className="font-medium text-pink-800">{reel.concept}</div>
                                  {reel.hook && <div className="text-pink-600 italic text-sm mt-1">Hook: {reel.hook}</div>}
                                  <p className="text-slate-700 text-sm mt-2 whitespace-pre-wrap">{reel.script}</p>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Twitter Posts */}
                    {viewingReportContent.twitter && (
                      <div>
                        <h3 className="text-lg font-bold text-purple-900 flex items-center gap-2">
                          <span className="text-blue-500">𝕏</span> Twitter/X Posts
                        </h3>
                        <div className="space-y-3 mt-2">
                          {/* New format: simple array */}
                          {Array.isArray(viewingReportContent.twitter) && viewingReportContent.twitter.map((post, i) => (
                            <div key={i} className="bg-blue-50 p-3 rounded border-l-4 border-blue-500">
                              <p className="text-slate-700 text-sm whitespace-pre-wrap">{post}</p>
                            </div>
                          ))}
                          {/* Old format: complex object */}
                          {!Array.isArray(viewingReportContent.twitter) && viewingReportContent.twitter.tweet_threads && (
                            <>
                              <div className="font-semibold text-blue-900 mt-2">Tweet Threads:</div>
                              {viewingReportContent.twitter.tweet_threads.map((thread, i) => (
                                <div key={`tt-${i}`} className="bg-blue-50 p-3 rounded border-l-4 border-blue-500">
                                  <div className="font-medium text-blue-800 mb-2">{thread.thread_topic}</div>
                                  {thread.tweets.map((tweet, j) => (
                                    <div key={j} className="ml-3 mb-1 text-slate-700 text-sm">{tweet}</div>
                                  ))}
                                </div>
                              ))}
                            </>
                          )}
                          {!Array.isArray(viewingReportContent.twitter) && viewingReportContent.twitter.single_tweets && (
                            <>
                              <div className="font-semibold text-blue-900 mt-3">Single Tweets:</div>
                              {viewingReportContent.twitter.single_tweets.map((tweet, i) => (
                                <div key={`st-${i}`} className="bg-blue-100 p-2 rounded">
                                  <p className="text-slate-700 text-sm">{tweet}</p>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* TikTok Posts */}
                    {viewingReportContent.tiktok && (
                      <div>
                        <h3 className="text-lg font-bold text-purple-900 flex items-center gap-2">
                          <span>🎵</span> TikTok Ideas
                        </h3>
                        <div className="space-y-3 mt-2">
                          {/* New format: simple array */}
                          {Array.isArray(viewingReportContent.tiktok) && viewingReportContent.tiktok.map((post, i) => (
                            <div key={i} className="bg-purple-50 p-3 rounded border-l-4 border-purple-500">
                              <p className="text-slate-700 text-sm whitespace-pre-wrap">{post}</p>
                            </div>
                          ))}
                          {/* Old format: complex object */}
                          {!Array.isArray(viewingReportContent.tiktok) && viewingReportContent.tiktok.video_concepts && (
                            viewingReportContent.tiktok.video_concepts.map((video, i) => (
                              <div key={i} className="bg-purple-50 p-3 rounded border-l-4 border-purple-500">
                                <div className="font-medium text-purple-800">{video.concept}</div>
                                {video.hook && <div className="text-purple-600 italic text-sm mt-1">Hook: {video.hook}</div>}
                                <p className="text-slate-700 text-sm mt-2 whitespace-pre-wrap">{video.script}</p>
                                {video.visual_notes && <div className="text-purple-600 text-xs mt-2">Visual: {video.visual_notes}</div>}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Creative Direction Report */}
                {viewingReport === 'creative_direction' && (
                  <div className="space-y-6">
                    {viewingReportContent.design_palette && (
                      <div className="bg-purple-50 p-4 rounded border-l-4 border-purple-500">
                        <h3 className="text-lg font-bold text-purple-900 mb-3">🎨 Design Palette</h3>
                        {viewingReportContent.design_palette.primary_colors && (
                          <div className="mb-3">
                            <h4 className="font-semibold text-slate-800">Primary Colors:</h4>
                            <div className="flex gap-2 mt-2">
                              {viewingReportContent.design_palette.primary_colors.map((color, i) => (
                                <div key={i} className="flex items-center gap-2">
                                  <div 
                                    className="w-12 h-12 rounded border-2 border-slate-300" 
                                    style={{ backgroundColor: color }}
                                  ></div>
                                  <span className="text-sm text-slate-600">{color}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {viewingReportContent.design_palette.mood && (
                          <div className="mb-2">
                            <h4 className="font-semibold text-slate-800">Mood:</h4>
                            <p className="text-slate-700">{viewingReportContent.design_palette.mood}</p>
                          </div>
                        )}
                        {viewingReportContent.design_palette.fonts && Array.isArray(viewingReportContent.design_palette.fonts) && (
                          <div>
                            <h4 className="font-semibold text-slate-800">Fonts:</h4>
                            <ul className="list-disc list-inside">
                              {viewingReportContent.design_palette.fonts.map((font, i) => (
                                <li key={i} className="text-slate-700">{font}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {viewingReportContent.image_prompts && (
                      <div>
                        <h3 className="text-lg font-bold text-purple-900 mb-3">📸 Image Prompts</h3>
                        {viewingReportContent.image_prompts.instagram_posts && Array.isArray(viewingReportContent.image_prompts.instagram_posts) && (
                          <div className="mb-4">
                            <h4 className="font-semibold text-slate-800 mb-2">Instagram Posts:</h4>
                            <div className="space-y-2">
                              {viewingReportContent.image_prompts.instagram_posts.map((prompt, i) => (
                                <div key={i} className="bg-pink-50 p-3 rounded border-l-4 border-pink-500">
                                  <p className="text-slate-700 text-sm">{prompt}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {viewingReportContent.image_prompts.facebook_banners && Array.isArray(viewingReportContent.image_prompts.facebook_banners) && (
                          <div>
                            <h4 className="font-semibold text-slate-800 mb-2">Facebook Banners:</h4>
                            <div className="space-y-2">
                              {viewingReportContent.image_prompts.facebook_banners.map((prompt, i) => (
                                <div key={i} className="bg-blue-50 p-3 rounded border-l-4 border-blue-500">
                                  <p className="text-slate-700 text-sm">{prompt}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {viewingReportContent.brand_cohesion && (
                      <div className="bg-blue-50 p-4 rounded border-l-4 border-blue-500">
                        <h3 className="text-lg font-bold text-blue-900 mb-3">🎯 Brand Cohesion</h3>
                        {viewingReportContent.brand_cohesion.visual_themes && Array.isArray(viewingReportContent.brand_cohesion.visual_themes) && (
                          <div className="mb-3">
                            <h4 className="font-semibold text-slate-800">Visual Themes:</h4>
                            <ul className="list-disc list-inside">
                              {viewingReportContent.brand_cohesion.visual_themes.map((theme, i) => (
                                <li key={i} className="text-slate-700">{theme}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {viewingReportContent.brand_cohesion.voice_guidelines && (
                          <div>
                            <h4 className="font-semibold text-slate-800">Voice Guidelines:</h4>
                            <p className="text-slate-700">{viewingReportContent.brand_cohesion.voice_guidelines}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Audience Insights Report */}
                {viewingReport === 'audience_insights' && (
                  <div className="space-y-6">
                    {typeof viewingReportContent === 'object' ? (
                      Object.entries(viewingReportContent).map(([key, value]) => (
                        <div key={key} className="bg-blue-50 p-4 rounded border-l-4 border-blue-500">
                          <h3 className="text-lg font-bold text-blue-900 mb-2 capitalize">{key.replace(/_/g, ' ')}</h3>
                          {Array.isArray(value) ? (
                            <ul className="list-disc list-inside space-y-1">
                              {value.map((item, i) => (
                                <li key={i} className="text-slate-700">{typeof item === 'object' ? JSON.stringify(item) : item}</li>
                              ))}
                            </ul>
                          ) : typeof value === 'object' ? (
                            <pre className="text-slate-700 text-sm whitespace-pre-wrap">{JSON.stringify(value, null, 2)}</pre>
                          ) : (
                            <p className="text-slate-700 whitespace-pre-wrap">{value}</p>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="bg-blue-50 p-4 rounded">
                        <p className="text-slate-700 whitespace-pre-wrap">{viewingReportContent}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Plans Report */}
                {viewingReport === 'action_plans' && (
                  <div className="space-y-6">
                    {viewingReportContent.thirty_day_plan && (
                      <div className="bg-green-50 p-4 rounded border-l-4 border-green-500">
                        <h3 className="text-lg font-bold text-green-900 mb-3">📅 30-Day Action Plan</h3>
                        {Array.isArray(viewingReportContent.thirty_day_plan) ? (
                          <div className="space-y-2">
                            {viewingReportContent.thirty_day_plan.map((item, i) => (
                              <div key={i} className="bg-white p-3 rounded">
                                <p className="text-slate-700">{typeof item === 'object' ? JSON.stringify(item) : item}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-700 whitespace-pre-wrap">{viewingReportContent.thirty_day_plan}</p>
                        )}
                      </div>
                    )}
                    {viewingReportContent.ninety_day_plan && (
                      <div className="bg-blue-50 p-4 rounded border-l-4 border-blue-500">
                        <h3 className="text-lg font-bold text-blue-900 mb-3">📅 90-Day Action Plan</h3>
                        {Array.isArray(viewingReportContent.ninety_day_plan) ? (
                          <div className="space-y-2">
                            {viewingReportContent.ninety_day_plan.map((item, i) => (
                              <div key={i} className="bg-white p-3 rounded">
                                <p className="text-slate-700">{typeof item === 'object' ? JSON.stringify(item) : item}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-700 whitespace-pre-wrap">{viewingReportContent.ninety_day_plan}</p>
                        )}
                      </div>
                    )}
                    {typeof viewingReportContent === 'string' && (
                      <div className="bg-green-50 p-4 rounded">
                        <p className="text-slate-700 whitespace-pre-wrap">{viewingReportContent}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Influencer Outreach Report */}
                {viewingReport === 'influencer_outreach' && (
                  <div className="space-y-6">
                    {viewingReportContent.pitch_templates && (
                      <div className="bg-blue-50 p-4 rounded border-l-4 border-blue-500">
                        <h3 className="text-lg font-bold text-blue-900 mb-3">📝 Pitch Templates</h3>
                        {Array.isArray(viewingReportContent.pitch_templates) ? (
                          <div className="space-y-3">
                            {viewingReportContent.pitch_templates.map((template, i) => (
                              <div key={i} className="bg-white p-3 rounded border border-blue-200">
                                <div className="font-semibold text-blue-900 mb-2">{template.title || `Template ${i + 1}`}</div>
                                <p className="text-slate-700 whitespace-pre-wrap">{template.content || template}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-700 whitespace-pre-wrap">{viewingReportContent.pitch_templates}</p>
                        )}
                      </div>
                    )}
                    
                    {viewingReportContent.partnership_proposals && (
                      <div className="bg-purple-50 p-4 rounded border-l-4 border-purple-500">
                        <h3 className="text-lg font-bold text-purple-900 mb-3">🤝 Partnership Proposals</h3>
                        {typeof viewingReportContent.partnership_proposals === 'object' && !Array.isArray(viewingReportContent.partnership_proposals) ? (
                          <div className="space-y-3">
                            {Object.entries(viewingReportContent.partnership_proposals).map(([key, value]) => (
                              <div key={key} className="bg-white p-3 rounded">
                                <div className="font-semibold text-purple-800 capitalize mb-2">{key.replace(/_/g, ' ')}</div>
                                <p className="text-slate-700 whitespace-pre-wrap">{value}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-slate-700 whitespace-pre-wrap">
                            {typeof viewingReportContent.partnership_proposals === 'string' 
                              ? viewingReportContent.partnership_proposals 
                              : JSON.stringify(viewingReportContent.partnership_proposals, null, 2)}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {viewingReportContent.collaboration_ideas && Array.isArray(viewingReportContent.collaboration_ideas) && (
                      <div className="bg-green-50 p-4 rounded border-l-4 border-green-500">
                        <h3 className="text-lg font-bold text-green-900 mb-3">💡 Collaboration Ideas</h3>
                        <ul className="list-disc list-inside space-y-2">
                          {viewingReportContent.collaboration_ideas.map((idea, i) => (
                            <li key={i} className="text-slate-700">{idea}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {typeof viewingReportContent === 'string' && (
                      <div className="bg-blue-50 p-4 rounded">
                        <p className="text-slate-700 whitespace-pre-wrap">{viewingReportContent}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Email Marketing Campaign Report */}
                {viewingReport === 'email_campaign' && (
                  <div className="space-y-6">
                    {viewingReportContent.pre_launch_sequence && Array.isArray(viewingReportContent.pre_launch_sequence) && (
                      <div className="bg-blue-50 p-4 rounded border-l-4 border-blue-500">
                        <h3 className="text-lg font-bold text-blue-900 mb-3">📨 Pre-Launch Email Sequence</h3>
                        <div className="space-y-4">
                          {viewingReportContent.pre_launch_sequence.map((email, i) => (
                            <div key={i} className="bg-white p-4 rounded border border-blue-200">
                              <div className="flex justify-between items-start mb-2">
                                <span className="font-semibold text-blue-900">Email {i + 1}</span>
                                <span className="text-sm bg-blue-100 px-2 py-1 rounded">{email.timing || email.day}</span>
                              </div>
                              <div className="font-medium text-slate-800 mb-2">Subject: {email.subject}</div>
                              <p className="text-slate-700 whitespace-pre-wrap">{email.content || email.body}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {viewingReportContent.launch_email && (
                      <div className="bg-green-50 p-4 rounded border-l-4 border-green-500">
                        <h3 className="text-lg font-bold text-green-900 mb-3">🚀 Launch Day Email</h3>
                        <div className="bg-white p-4 rounded">
                          <div className="font-medium text-slate-800 mb-2">Subject: {viewingReportContent.launch_email.subject}</div>
                          <p className="text-slate-700 whitespace-pre-wrap">{viewingReportContent.launch_email.content || viewingReportContent.launch_email.body}</p>
                        </div>
                      </div>
                    )}
                    
                    {viewingReportContent.post_launch_sequence && Array.isArray(viewingReportContent.post_launch_sequence) && (
                      <div className="bg-purple-50 p-4 rounded border-l-4 border-purple-500">
                        <h3 className="text-lg font-bold text-purple-900 mb-3">📬 Post-Launch Follow-Up</h3>
                        <div className="space-y-3">
                          {viewingReportContent.post_launch_sequence.map((email, i) => (
                            <div key={i} className="bg-white p-3 rounded border border-purple-200">
                              <div className="font-medium text-slate-800 mb-2">Subject: {email.subject}</div>
                              <p className="text-slate-700 whitespace-pre-wrap">{email.content || email.body}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {typeof viewingReportContent === 'string' && (
                      <div className="bg-blue-50 p-4 rounded">
                        <p className="text-slate-700 whitespace-pre-wrap">{viewingReportContent}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Hashtag & SEO Strategy Report */}
                {viewingReport === 'hashtag_seo' && (
                  <div className="space-y-6">
                    {viewingReportContent.platform_hashtags && (
                      <div className="bg-blue-50 p-4 rounded border-l-4 border-blue-500">
                        <h3 className="text-lg font-bold text-blue-900 mb-3">#️⃣ Platform-Specific Hashtags</h3>
                        {typeof viewingReportContent.platform_hashtags === 'object' ? (
                          <div className="space-y-3">
                            {Object.entries(viewingReportContent.platform_hashtags).map(([platform, hashtags]) => (
                              <div key={platform} className="bg-white p-3 rounded">
                                <div className="font-semibold text-slate-800 capitalize mb-2">{platform}:</div>
                                <div className="flex flex-wrap gap-2">
                                  {Array.isArray(hashtags) ? hashtags.map((tag, i) => (
                                    <span key={i} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                                      {tag.startsWith('#') ? tag : `#${tag}`}
                                    </span>
                                  )) : <span className="text-slate-700">{hashtags}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-700">{viewingReportContent.platform_hashtags}</p>
                        )}
                      </div>
                    )}
                    
                    {viewingReportContent.seo_keywords && Array.isArray(viewingReportContent.seo_keywords) && (
                      <div className="bg-green-50 p-4 rounded border-l-4 border-green-500">
                        <h3 className="text-lg font-bold text-green-900 mb-3">🔍 SEO Keywords</h3>
                        <div className="flex flex-wrap gap-2">
                          {viewingReportContent.seo_keywords.map((keyword, i) => (
                            <span key={i} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {viewingReportContent.trending_hashtags && Array.isArray(viewingReportContent.trending_hashtags) && (
                      <div className="bg-purple-50 p-4 rounded border-l-4 border-purple-500">
                        <h3 className="text-lg font-bold text-purple-900 mb-3">🔥 Trending Hashtags</h3>
                        <div className="flex flex-wrap gap-2">
                          {viewingReportContent.trending_hashtags.map((tag, i) => (
                            <span key={i} className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm">
                              {tag.startsWith('#') ? tag : `#${tag}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {viewingReportContent.metadata_optimization && (
                      <div className="bg-orange-50 p-4 rounded border-l-4 border-orange-500">
                        <h3 className="text-lg font-bold text-orange-900 mb-3">⚙️ Metadata Optimization</h3>
                        {typeof viewingReportContent.metadata_optimization === 'object' ? (
                          <div className="space-y-3">
                            {viewingReportContent.metadata_optimization.amazon_keywords && Array.isArray(viewingReportContent.metadata_optimization.amazon_keywords) && (
                              <div className="bg-white p-3 rounded">
                                <div className="font-semibold text-orange-900 mb-2">Amazon Keywords:</div>
                                <div className="flex flex-wrap gap-2">
                                  {viewingReportContent.metadata_optimization.amazon_keywords.map((kw, i) => (
                                    <span key={i} className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm">{kw}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {viewingReportContent.metadata_optimization.goodreads_shelves && Array.isArray(viewingReportContent.metadata_optimization.goodreads_shelves) && (
                              <div className="bg-white p-3 rounded">
                                <div className="font-semibold text-orange-900 mb-2">Goodreads Shelves:</div>
                                <div className="flex flex-wrap gap-2">
                                  {viewingReportContent.metadata_optimization.goodreads_shelves.map((shelf, i) => (
                                    <span key={i} className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm">{shelf}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {viewingReportContent.metadata_optimization.social_bio_keywords && (
                              <div className="bg-white p-3 rounded">
                                <div className="font-semibold text-orange-900 mb-2">Social Bio Keywords:</div>
                                <p className="text-slate-700">{viewingReportContent.metadata_optimization.social_bio_keywords}</p>
                              </div>
                            )}
                            {viewingReportContent.metadata_optimization.blog_post_titles && Array.isArray(viewingReportContent.metadata_optimization.blog_post_titles) && (
                              <div className="bg-white p-3 rounded">
                                <div className="font-semibold text-orange-900 mb-2">Blog Post Titles:</div>
                                <ul className="list-disc list-inside space-y-1">
                                  {viewingReportContent.metadata_optimization.blog_post_titles.map((title, i) => (
                                    <li key={i} className="text-slate-700">{title}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-slate-700 whitespace-pre-wrap">{viewingReportContent.metadata_optimization}</p>
                        )}
                      </div>
                    )}
                    
                    {viewingReportContent.hashtag_strategy && typeof viewingReportContent.hashtag_strategy === 'object' && (
                      <div className="bg-slate-50 p-4 rounded border-l-4 border-slate-500">
                        <h3 className="text-lg font-bold text-slate-900 mb-3">📊 Hashtag Strategy</h3>
                        <div className="space-y-2 text-slate-700">
                          {viewingReportContent.hashtag_strategy.posting_frequency && (
                            <div className="bg-white p-3 rounded">
                              <div className="font-semibold text-slate-800">Posting Frequency:</div>
                              <p className="mt-1">{viewingReportContent.hashtag_strategy.posting_frequency}</p>
                            </div>
                          )}
                          {viewingReportContent.hashtag_strategy.mix_guidance && (
                            <div className="bg-white p-3 rounded">
                              <div className="font-semibold text-slate-800">Mix Guidance:</div>
                              <p className="mt-1">{viewingReportContent.hashtag_strategy.mix_guidance}</p>
                            </div>
                          )}
                          {viewingReportContent.hashtag_strategy.engagement_tactics && (
                            <div className="bg-white p-3 rounded">
                              <div className="font-semibold text-slate-800">Engagement Tactics:</div>
                              <p className="mt-1">{viewingReportContent.hashtag_strategy.engagement_tactics}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {typeof viewingReportContent === 'string' && (
                      <div className="bg-blue-50 p-4 rounded">
                        <p className="text-slate-700 whitespace-pre-wrap">{viewingReportContent}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Launch Package Report */}
                {viewingReport === 'launch_package' && (
                  <div className="space-y-6">
                    {viewingReportContent.countdown_sequence && Array.isArray(viewingReportContent.countdown_sequence) && (
                      <div className="bg-orange-50 p-4 rounded border-l-4 border-orange-500">
                        <h3 className="text-lg font-bold text-orange-900 mb-3">⏰ Countdown Sequence</h3>
                        <div className="space-y-3">
                          {viewingReportContent.countdown_sequence.map((item, i) => (
                            <div key={i} className="bg-white p-3 rounded border border-orange-200">
                              <div className="font-semibold text-orange-900">{item.days_out}</div>
                              <p className="text-slate-700 mt-1">{item.post}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {viewingReportContent.launch_day_schedule && Array.isArray(viewingReportContent.launch_day_schedule) && (
                      <div className="bg-purple-50 p-4 rounded border-l-4 border-purple-500">
                        <h3 className="text-lg font-bold text-purple-900 mb-3">🚀 Launch Day Schedule</h3>
                        <div className="space-y-3">
                          {viewingReportContent.launch_day_schedule.map((item, i) => (
                            <div key={i} className="bg-white p-3 rounded border border-purple-200">
                              <div className="flex justify-between items-start mb-2">
                                <span className="font-semibold text-purple-900">{item.time}</span>
                                <span className="text-sm bg-purple-100 px-2 py-1 rounded">{item.platform}</span>
                              </div>
                              <p className="text-slate-700">{item.post}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {viewingReportContent.review_request_templates && (
                      <div className="bg-blue-50 p-4 rounded border-l-4 border-blue-500">
                        <h3 className="text-lg font-bold text-blue-900 mb-3">⭐ Review Request Templates</h3>
                        {Array.isArray(viewingReportContent.review_request_templates) ? (
                          <div className="space-y-2">
                            {viewingReportContent.review_request_templates.map((template, i) => (
                              <div key={i} className="bg-white p-3 rounded">
                                <p className="text-slate-700">{template}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-700 whitespace-pre-wrap">{viewingReportContent.review_request_templates}</p>
                        )}
                      </div>
                    )}
                    
                    {typeof viewingReportContent === 'string' && (
                      <div className="bg-orange-50 p-4 rounded">
                        <p className="text-slate-700 whitespace-pre-wrap">{viewingReportContent}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
              <div className="px-6 pb-6 flex gap-3 print:hidden">
                <button
                  onClick={handlePrintReport}
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                >
                  🖨️ Print Report
                </button>
                <button
                  onClick={handleDownloadReport}
                  className="px-4 py-2 bg-slate-600 text-white rounded hover:bg-slate-700 transition-colors"
                >
                  📄 Download Report
                </button>
              </div>
            </Card>
          )}

          {/* Generation Modal */}
          {generatingReport && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
              <div className="bg-white rounded-lg p-8 shadow-2xl max-w-md w-full mx-4">
                <div className="flex flex-col items-center justify-center space-y-4">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600"></div>
                  <p className="text-xl font-bold text-slate-800 text-center">
                    Generating Report
                  </p>
                  <p className="text-md text-slate-600 text-center">
                    {generatingReport === 'platform_posts_1' && 'Platform Posts - Facebook & Instagram'}
                    {generatingReport === 'platform_posts_2' && 'Platform Posts - Twitter & TikTok'}
                    {generatingReport === 'creative_direction' && 'Creative Direction & Visual Guide'}
                    {generatingReport === 'audience_insights' && 'Audience Insights & Targeting'}
                    {generatingReport === 'action_plans' && 'Action Plans'}
                    {generatingReport === 'launch_package' && 'Launch Package & Review Templates'}
                    {generatingReport === 'influencer_outreach' && 'Influencer & Partnership Outreach Kit'}
                    {generatingReport === 'email_campaign' && 'Email Marketing Campaign'}
                    {generatingReport === 'hashtag_seo' && 'Hashtag & SEO Strategy'}
                  </p>
                  <p className="text-sm text-slate-500 text-center">This may take 30-60 seconds</p>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  // Show form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100">
      {/* Header with Logo and Navigation */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/Digital_Ink_AI_Logo.png" 
                alt="Digital Ink AI Logo" 
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                }}
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/homepage')}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                🏠 Home
              </button>
              <button
                onClick={() => navigate('/user_dashboard')}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                📊 Dashboard
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Service Title and Description */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            📱 Social Media Marketing Kit
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Generate a complete social media promotional package for all major platforms including Facebook, Instagram, Twitter, LinkedIn, TikTok, and Pinterest. Receive platform-optimized posts with custom hashtags, engaging captions, posting schedules, and strategic content calendars designed to maximize engagement and drive book sales across your entire social media presence.
          </p>
        </div>

        {/* Pricing Info */}
        {pricingInfo && (
          <div className="mb-8 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
            <div className="text-center">
              {pricingInfo.payment_required ? (
                <div>
                  <h3 className="text-xl font-bold text-purple-900 mb-2">💎 Standard Service - ${pricingInfo.price}</h3>
                  <p className="text-purple-700">Professional social media marketing kit charged to your payment method on file</p>
                </div>
              ) : (
                <div>
                  <h3 className="text-xl font-bold text-green-900 mb-2">✨ Unlimited Free Access</h3>
                  <p className="text-green-700">Included with your subscription - generate as many marketing kits as you need!</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="text-center mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                    alt="Digital Ink AI" 
                    className="h-10 mx-auto mb-3"
                  />
                  <CardTitle className="text-2xl text-slate-900">📚 Book Information</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="book_title">Book Title *</Label>
                      <Input
                        id="book_title"
                        name="book_title"
                        value={formData.book_title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="subtitle">Subtitle</Label>
                      <Input
                        id="subtitle"
                        name="subtitle"
                        value={formData.subtitle}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="author_name">Author Name *</Label>
                      <Input
                        id="author_name"
                        name="author_name"
                        value={formData.author_name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="publisher_name">Publisher Name *</Label>
                      <Input
                        id="publisher_name"
                        name="publisher_name"
                        value={formData.publisher_name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="release_date">Release/Launch Date *</Label>
                      <Input
                        type="date"
                        id="release_date"
                        name="release_date"
                        value={formData.release_date}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="target_audience">Target Audience *</Label>
                      <select
                        id="target_audience"
                        name="target_audience"
                        value={formData.target_audience}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select target audience</option>
                        <option value="children">Children (Ages 0-12)</option>
                        <option value="young_adult">Young Adult (Ages 13-18)</option>
                        <option value="new_adult">New Adult (Ages 18-25)</option>
                        <option value="adult">Adult (Ages 25-65)</option>
                        <option value="senior">Senior (Ages 65+)</option>
                        <option value="general">General Audience</option>
                        <option value="academic">Academic/Scholarly</option>
                        <option value="professional">Business/Professional</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="publisher_website">Publisher Website *</Label>
                      <Input
                        type="url"
                        id="publisher_website"
                        name="publisher_website"
                        value={formData.publisher_website}
                        onChange={handleInputChange}
                        placeholder="https://publisherwebsite.com"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="author_website">Author Website (Optional)</Label>
                      <Input
                        type="url"
                        id="author_website"
                        name="author_website"
                        value={formData.author_website}
                        onChange={handleInputChange}
                        placeholder="https://authorwebsite.com"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="publication_status">Publication Status *</Label>
                    <select
                      id="publication_status"
                      name="publication_status"
                      value={formData.publication_status}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="pre-launch">Pre-Launch</option>
                      <option value="launching">Launching Soon</option>
                      <option value="published">Already Published</option>
                    </select>
                  </div>

                  {/* Book Formats */}
                  <div className="border-t-2 border-slate-200 pt-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">📚 Book Availability & Pricing (Optional)</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="md:col-span-2">
                        <Label htmlFor="hardcover_url">Hardcover URL</Label>
                        <Input
                          type="url"
                          id="hardcover_url"
                          name="hardcover_url"
                          value={formData.hardcover_url}
                          onChange={handleInputChange}
                          placeholder="https://amazon.com/your-book"
                        />
                      </div>
                      <div>
                        <Label htmlFor="hardcover_price">Price</Label>
                        <Input
                          type="number"
                          step="0.01"
                          id="hardcover_price"
                          name="hardcover_price"
                          value={formData.hardcover_price}
                          onChange={handleInputChange}
                          placeholder="29.99"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="md:col-span-2">
                        <Label htmlFor="paperback_url">Paperback URL</Label>
                        <Input
                          type="url"
                          id="paperback_url"
                          name="paperback_url"
                          value={formData.paperback_url}
                          onChange={handleInputChange}
                          placeholder="https://amazon.com/your-book"
                        />
                      </div>
                      <div>
                        <Label htmlFor="paperback_price">Price</Label>
                        <Input
                          type="number"
                          step="0.01"
                          id="paperback_price"
                          name="paperback_price"
                          value={formData.paperback_price}
                          onChange={handleInputChange}
                          placeholder="19.99"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="md:col-span-2">
                        <Label htmlFor="ebook_url">eBook URL</Label>
                        <Input
                          type="url"
                          id="ebook_url"
                          name="ebook_url"
                          value={formData.ebook_url}
                          onChange={handleInputChange}
                          placeholder="https://amazon.com/your-book"
                        />
                      </div>
                      <div>
                        <Label htmlFor="ebook_price">Price</Label>
                        <Input
                          type="number"
                          step="0.01"
                          id="ebook_price"
                          name="ebook_price"
                          value={formData.ebook_price}
                          onChange={handleInputChange}
                          placeholder="9.99"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <Label htmlFor="audiobook_url">Audiobook URL</Label>
                        <Input
                          type="url"
                          id="audiobook_url"
                          name="audiobook_url"
                          value={formData.audiobook_url}
                          onChange={handleInputChange}
                          placeholder="https://audible.com/your-book"
                        />
                      </div>
                      <div>
                        <Label htmlFor="audiobook_price">Price</Label>
                        <Input
                          type="number"
                          step="0.01"
                          id="audiobook_price"
                          name="audiobook_price"
                          value={formData.audiobook_price}
                          onChange={handleInputChange}
                          placeholder="24.99"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Manuscript Upload */}
                  <div className="border-t-2 border-slate-200 pt-6">
                    <Label htmlFor="manuscript_file">
                      Upload Manuscript or Sample * (.txt, .pdf, .doc, .docx - Max 20MB)
                    </Label>
                    <div className="mt-2">
                      <input
                        type="file"
                        id="manuscript_file"
                        accept=".txt,.pdf,.doc,.docx"
                        onChange={handleFileChange}
                        className="block w-full text-sm text-slate-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-lg file:border-0
                          file:text-sm file:font-semibold
                          file:bg-purple-50 file:text-purple-700
                          hover:file:bg-purple-100"
                        required
                      />
                    </div>
                    {manuscriptFile && (
                      <p className="mt-2 text-sm text-green-600">
                        ✓ {manuscriptFile.name} ({(manuscriptFile.size / 1024 / 1024).toFixed(2)} MB)
                      </p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="border-t-2 border-slate-200 pt-6">
                    <button 
                      type="submit" 
                      disabled={isProcessing}
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:bg-gray-400 text-white font-bold py-4 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isProcessing ? 'Generating Your Marketing Kit...' : 'Generate Social Media Marketing Kit with Digital Ink AI'}
                    </button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">🎯 Generation Features</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start">
                  <span className="text-purple-500 mr-2">✓</span>
                  <span>Strategic Messaging Framework</span>
                </div>
                <div className="flex items-start">
                  <span className="text-purple-500 mr-2">✓</span>
                  <span>Platform-optimized posts (Facebook, Instagram, Twitter, TikTok)</span>
                </div>
                <div className="flex items-start">
                  <span className="text-purple-500 mr-2">✓</span>
                  <span>Creative Direction & Visual Guide</span>
                </div>
                <div className="flex items-start">
                  <span className="text-purple-500 mr-2">✓</span>
                  <span>Audience Insights & Targeting Strategy</span>
                </div>
                <div className="flex items-start">
                  <span className="text-purple-500 mr-2">✓</span>
                  <span>30-day & 90-day Action Plans</span>
                </div>
                <div className="flex items-start">
                  <span className="text-purple-500 mr-2">✓</span>
                  <span>Launch Package & Review Templates</span>
                </div>
                <div className="flex items-start">
                  <span className="text-purple-500 mr-2">✓</span>
                  <span>Influencer Outreach Kit</span>
                </div>
                <div className="flex items-start">
                  <span className="text-purple-500 mr-2">✓</span>
                  <span>Email Marketing Campaign</span>
                </div>
                <div className="flex items-start">
                  <span className="text-purple-500 mr-2">✓</span>
                  <span>Hashtag & SEO Strategy</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {showChargeModal && chargeInfo && (
        <ServiceChargeModal
          isOpen={showChargeModal}
          onClose={() => setShowChargeModal(false)}
          onConfirm={handleChargeConfirm}
          serviceInfo={chargeInfo}
          user={user}
        />
      )}

      {isProcessing && (
        <ProcessingModal
          isOpen={isProcessing}
          message="Extracting intelligence and generating primary report..."
          submessage="This may take 1-2 minutes. Creating messaging framework."
        />
      )}

      {showEmailReceiptModal && lastTransactionId && (
        <EmailReceiptModal
          isOpen={showEmailReceiptModal}
          onClose={() => setShowEmailReceiptModal(false)}
          onSendReceipt={handleSendReceiptEmail}
          transactionId={lastTransactionId}
        />
      )}
    </div>
  );
};

export default SMMKPage;
